import React, { useEffect, useRef, useState } from 'react';

type SimulationCallViewProps = {
  title: string;
  username: string;
  isAISpeaking: boolean;
  elapsedSec?: number;
  startTimeMs?: number;
  remoteStream?: MediaStream | null;
  connecting?: boolean;
  connected?: boolean;
  contactName?: string;
};

const formatTime = (s: number) => {
  const mm = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
};

const getInitials = (username: string) => {
  const base = (username || '').split('-')[0] || username || 'customer';
  const letters = base.replace(/[^a-zA-Z]/g, '').toUpperCase();
  if (letters.length >= 2) return letters.slice(0, 2);
  if (letters.length === 1) return letters + '•';
  return 'CU';
};

const SimulationCallView: React.FC<SimulationCallViewProps> = ({ title, username, isAISpeaking, elapsedSec, startTimeMs, remoteStream, connecting, connected, contactName }) => {
  const initials = getInitials(username);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [nowMs, setNowMs] = useState<number>(Date.now());
  const [showConnected, setShowConnected] = useState<boolean>(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);

  // Smooth 1s timer updates
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Attach and play remote stream audio (hidden element)
  useEffect(() => {
    try {
      if (audioRef.current && remoteStream) {
        // @ts-ignore
        audioRef.current.srcObject = remoteStream;
        const playPromise = audioRef.current.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch(() => {});
        }
      }
    } catch {}
  }, [remoteStream]);

  // Circular waveform VU using Web Audio API (ethereal preset)
  useEffect(() => {
    if (!remoteStream) return;
    let disposed = false;
    const prevIntensityRef = { current: 0 } as { current: number };
    (async () => {
      try {
        // Lazy create AudioContext
        const ctx = audioCtxRef.current || new (window.AudioContext || (window as any).webkitAudioContext)();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(remoteStream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 128; // coarser for smoother motion
        analyser.smoothingTimeConstant = 0.92; // more lingering
        source.connect(analyser);
        analyserRef.current = analyser;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        const size = 200; // canvas size
        canvas.width = size * dpr;
        canvas.height = size * dpr;
        const ctx2 = canvas.getContext('2d');
        if (!ctx2) return;
        ctx2.scale(dpr, dpr);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const centerX = size / 2;
        const centerY = size / 2;
        const baseRadius = 78; // radius just outside avatar (avatar ~160px)
        const maxExtra = 14; // shorter bars
        const bars = 64; // more, thinner bars

        const render = () => {
          if (disposed) return;
          rafRef.current = requestAnimationFrame(render);
          analyser.getByteTimeDomainData(dataArray);
          // RMS
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            const v = (dataArray[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / bufferLength);
          const target = Math.min(1, rms * 2.0); // softer amplitude
          // Ease intensity to avoid spikes
          prevIntensityRef.current = prevIntensityRef.current + (target - prevIntensityRef.current) * 0.12;
          const intensity = prevIntensityRef.current;

          ctx2.clearRect(0, 0, size, size);
          // Draw radial bars
          for (let i = 0; i < bars; i++) {
            const t = i / bars;
            const angle = t * Math.PI * 2;
            const extra = maxExtra * intensity * (0.6 + 0.4 * Math.sin((Date.now() / 300) + i));
            const r1 = baseRadius;
            const r2 = baseRadius + extra;
            const x1 = centerX + r1 * Math.cos(angle);
            const y1 = centerY + r1 * Math.sin(angle);
            const x2 = centerX + r2 * Math.cos(angle);
            const y2 = centerY + r2 * Math.sin(angle);
            ctx2.strokeStyle = 'rgba(132, 51, 255, 0.4)';
            ctx2.lineWidth = 1.2;
            ctx2.beginPath();
            ctx2.moveTo(x1, y1);
            ctx2.lineTo(x2, y2);
            ctx2.stroke();
          }
        };
        render();
      } catch {}
    })();
    return () => {
      disposed = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (analyserRef.current) {
        try { analyserRef.current.disconnect(); } catch {}
        analyserRef.current = null;
      }
      // Keep audio context for reuse; do not close to avoid user gesture requirement
    };
  }, [remoteStream]);

  // Flash "Connected. You're Live" for 3s on connect
  useEffect(() => {
    if (connected) {
      setShowConnected(true);
      const t = setTimeout(() => setShowConnected(false), 3000);
      return () => clearTimeout(t);
    }
  }, [connected]);

  const effectiveElapsed = typeof startTimeMs === 'number'
    ? Math.floor((nowMs - startTimeMs) / 1000)
    : (elapsedSec || 0);
  return (
    <div className="flex flex-col h-full items-center justify-start">
      {/* Header */}
      <div className="w-full flex items-center justify-between mb-4 px-2">
        <h3 className="text-lg font-semibold text-gray-900">{title || 'Simulation'}</h3>
        <div className="text-sm text-gray-600">{formatTime(Math.max(0, effectiveElapsed))}</div>
      </div>

      {/* Center avatar with VU ring */}
      <div className="flex-1 w-full flex items-center justify-center">
        <div className="relative" style={{ width: 160, height: 160 }}>
          {/* Animated ring */}
          <div
            className={`absolute inset-0 rounded-full ${isAISpeaking ? 'animate-pulse' : ''}`}
            style={{
              boxShadow: isAISpeaking
                ? '0 0 0 8px rgba(132, 51, 255, 0.25), 0 0 0 20px rgba(1, 188, 212, 0.15)'
                : '0 0 0 6px rgba(132, 51, 255, 0.08)',
              transition: 'box-shadow 200ms ease-in-out'
            }}
          />
          {/* Canvas VU overlay (slightly larger than avatar) */}
          <div className="absolute" style={{ left: -20, top: -20, width: 200, height: 200, pointerEvents: 'none' }}>
            <canvas ref={canvasRef} width={200} height={200} style={{ width: '200px', height: '200px', opacity: isAISpeaking ? 1 : 0.35, transition: 'opacity 200ms ease-in-out' }} />
          </div>
          {isAISpeaking && (
            <>
              <div className="absolute inset-0 rounded-full border-4 border-purple-400 opacity-60 animate-ping" style={{ transform: 'scale(1.15)' }} />
              <div className="absolute inset-0 rounded-full border-4 border-cyan-400 opacity-40 animate-ping" style={{ transform: 'scale(1.35)' }} />
            </>
          )}
          {/* Circle */}
          <div
            className="absolute inset-0 rounded-full flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #f3f4f6 0%, #ffffff 100%)', border: '1px solid #e5e7eb' }}
          >
            <div className="text-3xl font-bold text-gray-700">{initials}</div>
          </div>
        </div>
      </div>

      {/* Username and connection status */}
      <div className="mb-2 text-sm text-gray-700">Username: {username}</div>
      <div className="h-5 mb-2 text-sm">
        {connecting ? (
          <div className="text-gray-600">Connecting to {contactName || 'customer'}...</div>
        ) : showConnected ? (
          <div className="text-green-700 transition-opacity duration-700">Connected. You're Live</div>
        ) : null}
      </div>

      {/* Hidden audio element to ensure remote audio playback */}
      <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />
    </div>
  );
};

export default SimulationCallView;


